import React from "react";
import {
  Button,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  patch,
  patchMobile,
  distributorLanding1,
  distributorLanding2,
  distributorLanding3,
} from "../../assets";
import { COMMON_MUI_STYLES } from "../../styles";
import LanguageSelector from "../../components/LanguageSelector";
import { useAppContext } from "../../context/AppContext";
import { useLogoNewSrc } from "../../hooks/useLogoNewSrc";
import AppFooter from "../../components/AppFooter";

const getDistributorLandingSrc = (language: string): string => {
  if (language === "es") {
    return distributorLanding3;
  }
  if (language === "pt") {
    return distributorLanding2;
  }
  return distributorLanding1;
};

// import { getSSOLoginUrl } from "../../utils/ssoLoginUrl";

const distributorLoginButtonSx = {
  ...COMMON_MUI_STYLES.primaryCtaButton,
  fontSize: {
    xs: "1.3rem",
    sm: "1.3rem",
    md: "1.5rem",
  },
  padding: {
    xs: "18px 28px",
    sm: "20px 32px",
    md: "12px 32px",
  },
  minHeight: {
    xs: "3.25rem",
    sm: "3rem",
    md: "3rem",
  },
};

// Shared props interface for mobile and desktop components
interface DistributorLoginPageViewProps {
  handleHLSkinTermsClick: (e: React.MouseEvent) => void;
  handleDistributorPrivacyClick: (e: React.MouseEvent) => void;
  handleOrboPrivacyClick: (e: React.MouseEvent) => void;
  handleHerbalifePrivacyClick: (e: React.MouseEvent) => void;
  handleSSOLogin: () => void;
  getTranslation: (key: string, defaultValue: string) => string;
  distributorLandingSrc: string;
  logoSrc: string;
}

// Mobile View Component
const MobileView: React.FC<DistributorLoginPageViewProps> = ({
  handleHLSkinTermsClick,
  handleDistributorPrivacyClick,
  handleOrboPrivacyClick,
  handleHerbalifePrivacyClick,
  handleSSOLogin,
  getTranslation,
  distributorLandingSrc,
  logoSrc,
}) => {
  return (
    <Box sx={{ 
      width: '100%', 
      height: '100dvh',
      minHeight: '100dvh',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header with Language Selector */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: '8px',
        width: '100%',
      }}>
        <LanguageSelector />
      </Box>

      {/* Main Content - Logo and Image in Columns for Mobile */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            width: '80%',
            height: '25%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem 0',
            gap: '1.5rem',
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="Company Logo"
            sx={{ 
              height: "100%",
              width: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Image Section */}
        <Box
          sx={{
            width: '100%',
            height: '85%',
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: '-100px',
          }}
        >
          <Box
            component="img"
            key={distributorLandingSrc}
            src={distributorLandingSrc}
            alt="Distributor Landing"
            sx={{
              width: 'auto',
              maxWidth: '100%',
              height: '95%',
              objectFit: 'cover',
              objectPosition: 'bottom',
            }}
          />
        </Box>
      </Box>
      
      {/* Background patch section */}
      <Box
        sx={{
          width: "100%",
          backgroundImage: `url(${patchMobile})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
          backgroundPosition: "top center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.5rem",
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "0.5rem",
          padding: "80px 0px 20px",
        }}>
          <Typography variant="h3" sx={{
            color: "var(--color-text-primary)",
            textAlign: "center",
            fontSize: {
              xs: "1.2rem",
              sm: "1.2rem",
              md: "1.2rem",
              lg: "1.4rem",
              xl: "1.4rem"
            },
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "1.4",
            maxWidth: "90%",
          }}>
            {getTranslation("txt999", "Distributor Login")}
          </Typography>
          <Typography
            sx={{
              color: "var(--color-text-primary)",
              textAlign: "center",
              fontSize: {
                xs: "1rem",
                sm: "1rem",
                md: "1.2rem",
                lg: "1.4rem",
                xl: "1.4rem"
              },
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "1.4",
              maxWidth: "90%",
            }}
          >
            {getTranslation("Txt1000", "View customer results, share scan links, and recommend products")}
          </Typography>

          <Typography
            sx={{
              color: "var(--color-text-primary)",
              textAlign: "center",
              fontSize: {
               xs: "0.625rem",    
               sm: "0.6875rem",    
               md: "0.75rem",   
               lg: "0.8125rem",   
               xl: "0.875rem"
             },
             fontStyle: "normal",
             fontWeight: 400,
             lineHeight: "1.4",
             maxWidth: "90%",
           }}
         >
          {getTranslation("Txt863", "By clicking on Login,")}{" "}{getTranslation("Txt864", "I agree to the")}{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: "var(--color-text-primary)",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 700,
              width: "auto",
              fontSize: {
                xs: "0.625rem",    
                sm: "0.6875rem",    
                md: "0.75rem",   
                lg: "0.8125rem",   
                xl: "0.875rem"
              },
              "&:hover": {
                color: "var(--color-text-primary)",
              },
            }}
            onClick={handleHLSkinTermsClick}
          >
            {getTranslation("Txt818", "HLSkin.AI Terms of Use")}
          </Typography>
          {" "}{getTranslation("Txt819", "and acknowledge the")}{" "}
          {(() => {
            const translationText = getTranslation("Txt820", "Orbo Privacy Policy, % Herbalife Privacy Policy % and Distributor Privacy Policy");
            const parts = translationText.split('%');
            
            return (
              <>
                {parts[0] && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: "var(--color-text-primary)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 700,
                      width: "auto",
                      fontSize: {
                        xs: "0.625rem",    
                        sm: "0.6875rem",    
                        md: "0.75rem",   
                        lg: "0.8125rem",   
                        xl: "0.875rem"
                      },
                      "&:hover": {
                        color: "var(--color-text-primary)",
                      },
                    }}
                    onClick={handleOrboPrivacyClick}
                  >
                    {parts[0].trim()}{" "}
                  </Typography>
                )}
                {parts[1] && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: "var(--color-text-primary)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: {
                        xs: "0.625rem",    
                        sm: "0.6875rem",    
                        md: "0.75rem",   
                        lg: "0.8125rem",   
                        xl: "0.875rem"
                      },
                      width: "auto",
                      "&:hover": {
                        color: "var(--color-text-primary)",
                      },
                    }}
                    onClick={handleHerbalifePrivacyClick}
                  >
                    {parts[1].trim()}{" "}
                  </Typography>
                )}
                {parts[2] && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: "var(--color-text-primary)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: {
                        xs: "0.625rem",    
                        sm: "0.6875rem",    
                        md: "0.75rem",   
                        lg: "0.8125rem",   
                        xl: "0.875rem"
                      },
                      fontWeight: 700,
                      width: "auto",
                      "&:hover": {
                        color: "var(--color-text-primary)",
                      },
                    }}
                    onClick={handleDistributorPrivacyClick}
                  >
                    {parts[2].trim()}
                  </Typography>
                )}
              </>
            );
          })()}
          </Typography>

          <Button
            variant="contained"
            onClick={handleSSOLogin}
            sx={distributorLoginButtonSx}
          >
            {getTranslation("Txt10", "Login")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Desktop View Component
const DesktopView: React.FC<DistributorLoginPageViewProps> = ({
  handleHLSkinTermsClick,
  handleDistributorPrivacyClick,
  handleOrboPrivacyClick,
  handleHerbalifePrivacyClick,
  handleSSOLogin,
  getTranslation,
  distributorLandingSrc,
  logoSrc,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          position: "relative",
          overflow: "hidden",
        }}
      >
      {/* Language Selector */}
      <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: "11" }}>
        <LanguageSelector />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          minHeight: '90%',
          height: '100%',
          position: "absolute"
        }}
      >
        {/* Left Side - Logo */}
        <Box
          sx={{
            width: '50%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            alignItems: 'center',
            padding: '2rem 0',
            gap: '2rem',
            textAlign: 'center',
            flexBasis: '50%',
          }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="Company Logo"
            sx={{ 
              height: "6rem",
              width: "auto",
              maxWidth: "300px",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Right Side - Image */}
        <Box
          sx={{
            width: '50%',
            height: '80%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            key={distributorLandingSrc}
            src={distributorLandingSrc}
            alt="Distributor Landing"
            sx={{
              maxWidth: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>

      {/* Bottom Section with Background */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          backgroundImage: `url(${patch})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
          backgroundPosition: "top center",
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.5rem",
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "0.5rem",
          padding: "80px 0px 20px",
        }}>
          <Typography variant="h3" sx={{
            color: "var(--color-text-primary)",
            textAlign: "center",
            fontSize: {
              xs: "0.875rem",    
              sm: "0.9375rem",  
              md: "1rem",
              lg: "1.3rem",
              xl: "1.3rem"
            },
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "1.4",
            maxWidth: "90%",
          }}>
            {getTranslation("txt999", "Acceso distribuidores")}
          </Typography>
          <Typography
            sx={{
              color: "var(--color-text-primary)",
              textAlign: "center",
              fontSize: {
                xs: "0.875rem",    
                sm: "0.9375rem",  
                md: "1rem",
                lg: "1.3rem",
                xl: "1.3rem"
              },
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "1.4",
              maxWidth: "90%",
            }}
          >
            {getTranslation("Txt1000", "Ver resultados de clientes, compartir enlaces de escaneo y recomendar productos")}
          </Typography>

          <Typography
            sx={{
              color: "var(--color-text-primary)",
              textAlign: "center",
              fontSize: {
                xs: "0.625rem",    
                sm: "0.6875rem",    
                md: "0.75rem",   
                lg: "0.8125rem",   
                xl: "0.875rem"
              },
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "1.4",
              maxWidth: "90%",
            }}
          >
            {getTranslation("Txt863", "Al hacer clic en Iniciar sesión,")}{" "}{getTranslation("Txt864", "estás de acuerdo con los")}{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: "var(--color-text-primary)",
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 700,
                width: "auto",
                fontSize: {
                  xs: "0.625rem",    
                  sm: "0.6875rem",    
                  md: "0.75rem",   
                  lg: "0.8125rem",   
                  xl: "0.875rem"
                },
                "&:hover": {
                  color: "var(--color-text-primary)",
                },
              }}
              onClick={handleHLSkinTermsClick}
            >
              {getTranslation("Txt818", "Términos de uso de HLSkin.AI")}
            </Typography>
            {" "}{getTranslation("Txt819", "y reconoces la")}{" "}
            {(() => {
              const translationText = getTranslation("Txt820", "Política de privacidad de Orbo, % la Política de privacidad de Herbalife % y la Política de privacidad del Distribuidor.");
              const parts = translationText.split('%');
              
              return (
                <>
                  {parts[0] && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: "var(--color-text-primary)",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 700,
                        width: "auto",
                        fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                        "&:hover": {
                          color: "var(--color-text-primary)",
                        },
                      }}
                      onClick={handleOrboPrivacyClick}
                    >
                      {parts[0].trim()}{" "}
                    </Typography>
                  )}
                  {parts[1] && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: "var(--color-text-primary)",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                        width: "auto",
                        "&:hover": {
                          color: "var(--color-text-primary)",
                        },
                      }}
                      onClick={handleHerbalifePrivacyClick}
                    >
                      {parts[1].trim()}{" "}
                    </Typography>
                  )}
                  {parts[2] && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: "var(--color-text-primary)",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                        fontWeight: 700,
                        width: "auto",
                        "&:hover": {
                          color: "var(--color-text-primary)",
                        },
                      }}
                      onClick={handleDistributorPrivacyClick}
                    >
                      {parts[2].trim()}
                    </Typography>
                  )}
                </>
              );
            })()}
          </Typography>

          <Button
            variant="contained"
            onClick={handleSSOLogin}
            sx={distributorLoginButtonSx}
          >
            {getTranslation("Txt10", "Iniciar sesión")}
          </Button>
        </Box>
      </Box>
      </Box>
      <AppFooter sx={{ padding: "10px 15px" }} />
    </Box>
  );
};

const DistributorLoginPage: React.FC = () => {
  const { setCurrentView, getTranslation, countryData, currentLanguage } = useAppContext();

  const distributorLandingSrc = getDistributorLandingSrc(currentLanguage);
  const logoSrc = useLogoNewSrc();
  
  // Get SSO language code directly from country data
  const getSsoLanguageCode = (): string => {
    const savedLanguage = currentLanguage || 'es';
    // Find the matching country data entry
    const selectedCountryData = countryData.find(
      country => country.country_language_code === savedLanguage
    );
    
    // Use sso_language_code if available, otherwise fallback to savedLanguage or en-GB
    return selectedCountryData?.sso_language_code || 
           (savedLanguage === 'en' ? 'en-GB' : savedLanguage) || 
           'en-GB';
  };
  

  const handleHLSkinTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sessionStorage.setItem('privacyPolicyPrevView', 'distributor-login');
    sessionStorage.setItem('selectedPolicy', 'distributor-privacy-terms');
    setCurrentView('privacy-policy');
  };

  const handleDistributorPrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
   sessionStorage.setItem('privacyPolicyPrevView', 'distributor-login');
   sessionStorage.setItem('selectedPolicy', 'data-privacy-terms');
   setCurrentView('privacy-policy');
  };

  const handleOrboPrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sessionStorage.setItem('privacyPolicyPrevView', 'distributor-login');
    sessionStorage.setItem('selectedPolicy', 'orbo-privacy');
    setCurrentView('privacy-policy');
  };

  const handleHerbalifePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('https://www.herbalife.com/en-us/footer/privacy-policy-by-country', '_blank', 'noopener,noreferrer');
  };

  const handleSSOLogin = () => {
     const ssoUrl = `${import.meta.env.VITE_SSO_BASE_URL}/?client_id=${import.meta.env.VITE_SSO_CLIENT_ID}&locale=${getSsoLanguageCode()}&redirect_uri=${import.meta.env.VITE_SSO_REDIRECT_URI}`;
    //  const ssoUrl = `${import.meta.env.VITE_SSO_BASE_URL}/?client_id=${import.meta.env.VITE_SSO_CLIENT_ID}&locale=${getSsoLanguageCode()}&redirect_uri=${getSSOLoginUrl()}`;
     window.location.assign(ssoUrl);
  };

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const viewProps: DistributorLoginPageViewProps = {
    handleHLSkinTermsClick,
    handleDistributorPrivacyClick,
    handleOrboPrivacyClick,
    handleHerbalifePrivacyClick,
    handleSSOLogin,
    getTranslation,
    distributorLandingSrc,
    logoSrc,
  };

  if (isDesktop) {
    return <DesktopView {...viewProps} />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        backgroundColor: "#FFFFFF",
      }}
    >
      <MobileView {...viewProps} />
      <AppFooter sx={{ padding: "10px 15px" }} />
    </Box>
  );
};

export default DistributorLoginPage; 